import classNames from 'classnames';
import React, { useState, useMemo } from 'react';

import { Chip, LinkButton } from '@eika/button';

import type { Currency } from '../../../../generated-types';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { formatAmountRange } from '../AmountRangeInput';
import { useTransactionFilterContext } from '../TransactionFilterContext/TransactionFilterContext';
import { formatDateRangeMessage } from '../utils/getSearchDates';
import useFocusElementOnExit from './useFocusElementOnExit';

import './SearchResetButtons.scss';

type BasicSectionProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

interface SearchResetButtonProps extends BasicSectionProps {
    getTransactionList: () => void;
    focusElementOnExit: HTMLElement | null;
    buttonRemoveLabel: string;
    buttonEditLabel: string;
    infoMessage?: string;
    setShow: (value: boolean) => void;
    targetCurrency: Currency;
}

const SearchResetButtons: React.FC<SearchResetButtonProps> = ({
    className,
    children,
    focusElementOnExit,
    buttonRemoveLabel,
    buttonEditLabel,
    infoMessage,
    getTransactionList,
    setShow,
    targetCurrency,
    ...rest
}) => {
    const translation = useTranslation();
    const {
        resetSearch,
        transactionCategory,
        dateRange,
        freetext,
        minAmount,
        maxAmount,
        isFilteringState: [isFiltering],
        moneyDirection,
    } = useTransactionFilterContext();

    const searchResetButtons: { value?: string; reset?: () => void; isToggle?: boolean }[] = useMemo(() => {
        const { value: categories } = transactionCategory;
        const { fromDate: actualFromDate, toDate: actualToDate } = dateRange;
        const { value: targetMinAmount } = minAmount;
        const { value: targetMaxAmount } = maxAmount;
        const { value: searchText } = freetext;

        if (!isFiltering) return [];

        const toAndFromDate = formatDateRangeMessage(actualFromDate.value, actualToDate.value, {
            lastMonth: translation.transactionFilter.lastMonth,
            lastMonths: translation.transactionFilter.lastMonths,
        });
        const prefix =
            moneyDirection.value === 'sent'
                ? translation.transactionFilter.sent
                : translation.transactionFilter.received;

        const toAndFromAmount = formatAmountRange(targetMinAmount, targetMaxAmount, prefix, targetCurrency);
        const categoryButtons = categories.map((cat) => ({
            value: translation.transactionCategories[cat],
            reset: () => transactionCategory.removeFromArray(cat),
        }));

        const toAndFromAmountReset = () => {
            minAmount.reset();
            maxAmount.reset();
            moneyDirection.reset();
        };

        const resetButtons = [
            ...categoryButtons,
            {
                value: toAndFromDate ?? format(translation.transactionFilter.lastMonths, 12),
                reset: () => setShow(true),
                isToggle: true,
            },
            {
                value: toAndFromAmount,
                reset: toAndFromAmountReset,
            },
            {
                value: searchText,
                reset: freetext.reset,
            },
        ].filter((x) => !!x.value);

        if (resetButtons.length === 1) {
            resetButtons[0].reset = () => {
                resetSearch();
                getTransactionList();
            };
            resetButtons[0].isToggle = false;
        }
        return resetButtons;
    }, [
        transactionCategory,
        dateRange,
        minAmount,
        maxAmount,
        freetext,
        isFiltering,
        translation.transactionFilter.lastMonths,
        translation.transactionCategories,
        translation.transactionFilter.lastMonth,
        targetCurrency,
        setShow,
        resetSearch,
        getTransactionList,
        moneyDirection,
        translation.transactionFilter.sent,
        translation.transactionFilter.received,
    ]);

    useFocusElementOnExit(focusElementOnExit);
    const [elementToFocusOnExit, setElementToFocusOnExit] = useState<HTMLElement | null>(null);

    const resetAll = { call: resetSearch, label: translation.transactionFilter.resetFilter };
    return (
        <section className={classNames('search-reset-button__wrapper', className)} {...rest}>
            {children}
            <section className="search-reset-buttons" ref={(el) => setElementToFocusOnExit(el)} tabIndex={-1}>
                {searchResetButtons.map((resetButton) =>
                    resetButton.isToggle ? (
                        <Chip.Toggle
                            key={resetButton.value}
                            className="search-reset-button"
                            active
                            showXOnActive={false}
                            onClick={resetButton.reset}
                            aria-label={format(buttonEditLabel, resetButton.value ?? '')}
                        >
                            <span>{resetButton.value}</span>
                        </Chip.Toggle>
                    ) : (
                        <Chip.Removable
                            key={resetButton.value}
                            className="search-reset-button"
                            elementToFocusOnExit={elementToFocusOnExit}
                            onClick={resetButton.reset}
                            aria-label={format(buttonRemoveLabel, resetButton.value ?? '')}
                        >
                            <span>{resetButton.value}</span>
                        </Chip.Removable>
                    ),
                )}
            </section>
            <div className="search-reset__button-wrapper">
                {resetAll && (
                    <LinkButton onClick={resetAll.call} className="search-reset-all-button">
                        {resetAll.label}
                    </LinkButton>
                )}
            </div>
            {infoMessage ? <span className="search-reset-button__message">{infoMessage}</span> : null}
        </section>
    );
};

export default SearchResetButtons;
